// FroggyWorking — the ONE waiting indicator both customer FroggyAI frames
// render (the floating bubble and the full page) while a turn has no text
// yet — and, with its own `lines`, the staff FroggerAI panel (dock and
// standalone frame, waiting batch 2026-10-21; FroggerAI never had quips
// of its own before — its panel only ever said "Thinking..."). ONE set of
// mechanics; each surface brings its lines, and every line in this file
// is swept by the same test. Operator ruling 2026-10-10: FroggyAI does not narrate its searches,
// and a bare "…" for ten seconds is a dead wait. So: three soft dots and a
// short line in the shop's voice that changes every few seconds.
//
// THE LINES ARE MOOD, NEVER STATUS. None of them may name a source, a
// person or an action on data — no guides, no notes, no "checking with the
// frog room" (a person, beside a separate "Talk to a human" button), no
// counts, no percentages, no "searching" — and no promise of time or of
// being nearly done ("One sec…", "Almost there…"): the page's heartbeat
// can read "Still working… (40s)" right beside it. The frame cannot know what the
// model is doing, and a line that pretends to is a promise nothing keeps;
// the backend's own tool status ("Searching the web…") can sit right under
// it. A test sweeps every line.
//
// It opens on a calm neutral line and only starts rotating after
// FIRST_ROTATION_MS, so a fast answer never flashes a quip. It is NOT a live
// region of its own: the bubble's role="log" panel announces the inserted
// visually-hidden sentence once, and on the page the existing status /
// heartbeat regions stay the announcers. The dots and the rotating line are
// aria-hidden so nothing is re-read every few seconds. Under
// prefers-reduced-motion nothing animates and nothing rotates.
//
// COLOUR IS SET ON THE INNER SPANS THEMSELVES. global.css paints every bare
// span with var(--ff-color-text) (#111), and that element rule beats a colour
// merely INHERITED from .froggy-working — so the muted colour is declared on
// .froggy-working-line and on each dot, where it takes effect. Both frames
// draw their assistant bubble on a light ground in either theme, and
// --ff-text-muted has no dark override, so the line stays 7:1 on white in
// both themes. The staff panel's assistant bubble IS dark in dark mode
// (#374151), so FroggerAIChatPanel.css restates the line and dot colour
// for that bubble under the dark theme.

import { useEffect, useState } from 'react';

export const FROGGY_WORKING_NEUTRAL = 'Thinking…';

export const FROGGY_WORKING_LINES = [
  'Hmm, let me see…',
  'Working on it…',
  'Mulling it over on a lily pad…',
  'Thinking it over…',
  'Bear with me…',
  'Giving it some thought…',
];

// The staff panel's lines, under the SAME rules (mood, never status: no
// source, person, action on data, count or promise of time). FroggerAI's
// panel already has a status row of its own ("Thinking…", "Calling
// getMadeFoods…") right under the composer, so the staff indicator opens on
// the dots ALONE (`FROGGER_WORKING_NEUTRAL` is empty) — the word "Thinking…"
// stays that row's, one wait and one token (the 2026-09-13 ruling).
export const FROGGER_WORKING_NEUTRAL = '';

export const FROGGER_WORKING_LINES = [
  'Mulling it over…',
  'Pondering on a lily pad…',
  'Ribbit… hmm…',
  'Chewing on that one…',
  'Turning it over in my head…',
  'Croaking it through…',
];

export const FROGGER_WORKING_SR = 'FroggerAI is working on an answer';

export const FIRST_ROTATION_MS = 2500;
export const ROTATION_MS = 2800;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return Boolean(window.matchMedia(REDUCED_MOTION_QUERY).matches);
  } catch (e) {
    return false;
  }
}

export const FROGGY_WORKING_CSS = `
  .froggy-working {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.92em;
    font-style: italic;
    line-height: 1.4;
    min-height: 1.4em;
  }
  .froggy-working-dots {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
  }
  .froggy-working-dots span {
    color: var(--ff-text-muted, #495057);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.35;
    animation: froggy-working-dot 1.4s infinite ease-in-out;
  }
  .froggy-working-dots span:nth-child(2) { animation-delay: 0.18s; }
  .froggy-working-dots span:nth-child(3) { animation-delay: 0.36s; }
  .froggy-working-line {
    color: var(--ff-text-muted, #495057);
    display: inline-block;
    animation: froggy-working-fade 0.45s ease-out;
  }
  .froggy-working-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  @keyframes froggy-working-dot {
    0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-2px); }
  }
  @keyframes froggy-working-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .froggy-working-dots span,
    .froggy-working-line {
      animation: none;
    }
    .froggy-working-dots span { opacity: 0.6; }
  }
`;

export default function FroggyWorking({
  lines = FROGGY_WORKING_LINES,
  neutral = FROGGY_WORKING_NEUTRAL,
  label = 'FroggyAI is working on an answer',
}) {
  const [reduced] = useState(prefersReducedMotion);
  // -1 = the neutral opening line; 0..n-1 = a quip.
  const [index, setIndex] = useState(-1);
  const count = lines.length;

  useEffect(() => {
    if (reduced || count === 0) return undefined;
    let interval = null;
    const first = setTimeout(() => {
      setIndex(0);
      interval = setInterval(() => {
        setIndex((i) => (i + 1) % count);
      }, ROTATION_MS);
    }, FIRST_ROTATION_MS);
    return () => {
      clearTimeout(first);
      if (interval) clearInterval(interval);
    };
  }, [reduced, count]);

  const line = index < 0 ? neutral : lines[index % count];

  return (
    <span className="froggy-working" data-froggy-working>
      <style>{FROGGY_WORKING_CSS}</style>
      <span className="froggy-working-sr">{label}</span>
      <span className="froggy-working-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      {/* Keyed by the line so each new one mounts and fades in. */}
      <span key={index} className="froggy-working-line" aria-hidden="true" data-froggy-working-line>
        {line}
      </span>
    </span>
  );
}
